/**
 * Google Authentication Service
 * Handles Google OAuth 2.0 authentication without Firebase
 */

class GoogleAuthService {
  constructor() {
    this.clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    this.redirectUri = import.meta.env.VITE_GOOGLE_REDIRECT_URI || `${window.location.origin}/auth/google/callback`;
    this.scope = 'openid email profile';
    this.isInitialized = false;
  }

  /**
   * Initialize Google Sign-In
   */
  async initialize() {
    if (this.isInitialized) return;

    try {
      // Load Google Identity Services script
      await this.loadGoogleScript();

      // Initialize Google Identity Services
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: this.clientId,
          callback: this.handleCredentialResponse.bind(this),
          auto_select: false,
          cancel_on_tap_outside: true
        });

        this.isInitialized = true;
      } else {
        throw new Error('Google Identity Services not loaded');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Load Google Identity Services script
   */
  loadGoogleScript() {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (window.google && window.google.accounts) {
        resolve();
        return;
      }

      // Check if script tag already exists
      const existingScript = document.querySelector('script[src*="accounts.google.com"]');
      if (existingScript) {
        existingScript.onload = resolve;
        existingScript.onerror = reject;
        return;
      }

      // Create and load script
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Failed to load Google Identity Services script'));

      document.head.appendChild(script);
    });
  }

  /**
   * Handle credential response from Google
   */
  handleCredentialResponse(response) {
    if (this.credentialCallback) {
      this.credentialCallback(response);
    }
  }

  /**
   * Sign in with Google using One Tap
   */
  async signInWithOneTap() {
    try {
      await this.initialize();

      return new Promise((resolve, reject) => {
        this.credentialCallback = (response) => {
          if (response.credential) {
            resolve(response);
          } else {
            reject(new Error('No credential received'));
          }
        };

        // Display One Tap prompt
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            // Fall back to popup
            this.signInWithPopup().then(resolve).catch(reject);
          }
        });
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Sign in with Google using popup
   */
  async signInWithPopup() {
    try {
      await this.initialize();

      return new Promise((resolve, reject) => {
        this.credentialCallback = (response) => {
          if (response.credential) {
            resolve(response);
          } else {
            reject(new Error('No credential received'));
          }
        };

        // Render sign-in button (invisible, will be clicked programmatically)
        const buttonDiv = document.createElement('div');
        buttonDiv.style.display = 'none';
        document.body.appendChild(buttonDiv);

        window.google.accounts.id.renderButton(buttonDiv, {
          theme: 'outline',
          size: 'large',
          type: 'standard'
        });

        // Trigger click on the button
        setTimeout(() => {
          const button = buttonDiv.querySelector('div[role="button"]');
          if (button) {
            button.click();
          } else {
            // Fallback: use OAuth 2.0 flow
            this.signInWithOAuth().then(resolve).catch(reject);
          }

          // Cleanup
          document.body.removeChild(buttonDiv);
        }, 100);
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Sign in with Google using OAuth 2.0 redirect flow
   */
  async signInWithOAuth() {
    try {
      const authUrl = this.buildAuthUrl();

      // For development, open in popup
      if (import.meta.env.DEV) {
        return this.openAuthPopup(authUrl);
      } else {
        // For production, redirect
        window.location.href = authUrl;
        return new Promise(() => {}); // Never resolves as page will redirect
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Build Google OAuth authorization URL
   */
  buildAuthUrl() {
    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      response_type: 'code',
      scope: this.scope,
      access_type: 'offline',
      prompt: 'consent',
      state: this.generateState()
    });

    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  }

  /**
   * Open auth popup for development
   */
  openAuthPopup(url) {
    return new Promise((resolve, reject) => {
      const popup = window.open(url, 'google-auth', 'width=500,height=600,scrollbars=yes,resizable=yes');

      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          reject(new Error('Popup closed by user'));
        }
      }, 1000);

      // Listen for message from popup
      const messageListener = (event) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
          popup.close();
          resolve(event.data.credential);
        } else if (event.data.type === 'GOOGLE_AUTH_ERROR') {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
          popup.close();
          reject(new Error(event.data.error));
        }
      };

      window.addEventListener('message', messageListener);
    });
  }

  /**
   * Generate state parameter for OAuth
   */
  generateState() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  /**
   * Decode JWT token from Google
   */
  decodeJWT(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Extract user info from Google credential
   */
  extractUserInfo(credential) {
    try {
      const decoded = this.decodeJWT(credential);
      return {
        id: decoded.sub,
        email: decoded.email,
        firstName: decoded.given_name || '',
        lastName: decoded.family_name || '',
        fullName: decoded.name || '',
        picture: decoded.picture || '',
        emailVerified: decoded.email_verified || false,
        provider: 'google'
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Sign out from Google
   */
  async signOut() {
    try {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.disableAutoSelect();
      }
    } catch (error) {
    }
  }

  /**
   * Check if Google Sign-In is available
   */
  isAvailable() {
    return !!(this.clientId && window.google && window.google.accounts);
  }
}

// Create and export singleton instance
const googleAuthService = new GoogleAuthService();
export default googleAuthService;
